import { useRef, useState } from "react";
import { api, API, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, ImagePlus, X, FileText, Download, MapPin, DoorOpen, Map as MapIcon, PlayCircle } from "lucide-react";
import { toast } from "sonner";
import { InventoryReview } from "@/components/VideoInventory";

const CHUNK = 4 * 1024 * 1024;
const MAX_VIDEO = 100 * 1024 * 1024;
const MAX_IMAGE = 15 * 1024 * 1024;
const MAX_PLAN = 10 * 1024 * 1024;
export const emptyAccess = () => ({ origin: { notes: "", media: [] }, destination: { notes: "", media: [] } });

export async function uploadMedia(file) {
  if (file.type.startsWith("video/")) {
    if (file.size > MAX_VIDEO) throw new Error("Videos must be 100MB or smaller");
    const uploadId = Array.from(crypto.getRandomValues(new Uint8Array(16)), (b) => b.toString(16).padStart(2, "0")).join("");
    const total = Math.ceil(file.size / CHUNK);
    for (let i = 0; i < total; i++) {
      const fd = new FormData();
      fd.append("chunk", file.slice(i * CHUNK, (i + 1) * CHUNK), "chunk.bin");
      await api.post(`/inventory/upload-chunk?upload_id=${uploadId}&index=${i}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
    }
    const { data } = await api.post("/media/complete-video", { upload_id: uploadId, filename: file.name || "video.mp4", total_chunks: total });
    return { ...data, filename: file.name };
  }
  if (file.size > MAX_IMAGE) throw new Error("Photos must be 15MB or smaller");
  const fd = new FormData();
  fd.append("file", file);
  const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
  return { ...data, filename: file.name };
}

const errMsg = (e) => e.message && !e.response ? e.message : formatApiErrorDetail(e.response?.data?.detail) || "Upload failed";

function MediaThumb({ m, onRemove, testid }) {
  const src = `${API}/files/${m.path}`;
  return (
    <div className="relative h-24 w-24 rounded-lg overflow-hidden border border-border bg-black" data-testid={testid}>
      {m.content_type?.startsWith("video/")
        ? <a href={src} target="_blank" rel="noreferrer" className="h-full w-full flex items-center justify-center text-white"><PlayCircle className="h-8 w-8" /></a>
        : <a href={src} target="_blank" rel="noreferrer"><img src={src} alt="" className="h-full w-full object-cover" /></a>}
      {onRemove && <button type="button" onClick={onRemove} data-testid={`${testid}-remove`} className="absolute top-1 right-1 h-5 w-5 rounded-full bg-black/60 text-white flex items-center justify-center"><X className="h-3 w-3" /></button>}
    </div>
  );
}

function AccessSide({ side, label, value, update, accent }) {
  const [busy, setBusy] = useState(false);
  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    setBusy(true);
    try {
      for (const f of files) {
        const m = await uploadMedia(f);
        update((s) => ({ ...s, media: [...s.media, { path: m.path, content_type: m.content_type, filename: m.filename }] }));
      }
      toast.success("Access media uploaded");
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-3" data-testid={`access-${side}`}>
      <Label className="flex items-center gap-2"><MapPin className={`h-4 w-4 ${accent}`} /> {label}</Label>
      <Textarea value={value.notes} onChange={(e) => { const notes = e.target.value; update((s) => ({ ...s, notes })); }} rows={3} maxLength={2000} data-testid={`access-${side}-notes`}
        placeholder="e.g. 3rd floor, no lift, narrow stairs. Parking in loading zone out front." />
      <div className="flex flex-wrap gap-2">
        {value.media.map((m, i) => <MediaThumb key={m.path} m={m} testid={`access-${side}-media-${i}`} onRemove={() => update((s) => ({ ...s, media: s.media.filter((x) => x.path !== m.path) }))} />)}
        {value.media.length < 12 && (
          <label className="h-24 w-24 rounded-lg border-2 border-dashed border-border flex flex-col items-center justify-center gap-1 cursor-pointer hover:border-kinetic transition-colors text-[10px] text-muted-foreground text-center">
            {busy ? <Loader2 className="h-5 w-5 animate-spin text-kinetic" /> : <><ImagePlus className="h-5 w-5" />Photo / video</>}
            <input type="file" accept="image/*,video/*" multiple className="hidden" disabled={busy} onChange={onFiles} data-testid={`access-${side}-upload`} />
          </label>
        )}
      </div>
    </div>
  );
}

// onChange must be a React state setter (functional updates keep concurrent uploads/typing safe)
export function AccessEditor({ value, onChange }) {
  const v = value || emptyAccess();
  const upd = (side) => (fn) => onChange((prev) => { const p = prev || emptyAccess(); return { ...p, [side]: fn(p[side]) }; });
  return (
    <div className="grid sm:grid-cols-2 gap-6" data-testid="access-editor">
      <AccessSide side="origin" label="Pickup access" accent="text-emerald-500" value={v.origin} update={upd("origin")} />
      <AccessSide side="destination" label="Drop-off access" accent="text-kinetic" value={v.destination} update={upd("destination")} />
    </div>
  );
}

export function AccessView({ access, legacyNotes }) {
  const sides = [["origin", "Pickup access", "text-emerald-500"], ["destination", "Drop-off access", "text-kinetic"]]
    .filter(([k]) => access?.[k]?.notes || access?.[k]?.media?.length);
  if (!sides.length && !legacyNotes) return null;
  return (
    <div className="space-y-4" data-testid="job-access-view">
      {legacyNotes && <div><p className="font-mono text-xs text-muted-foreground uppercase mb-1">Access notes</p><p className="text-sm leading-relaxed">{legacyNotes}</p></div>}
      <div className="grid sm:grid-cols-2 gap-5">
        {sides.map(([k, label, accent]) => (
          <div key={k} data-testid={`job-access-${k}`}>
            <p className="font-mono text-xs text-muted-foreground uppercase mb-1 flex items-center gap-1"><MapPin className={`h-3.5 w-3.5 ${accent}`} /> {label}</p>
            {access[k].notes && <p className="text-sm leading-relaxed whitespace-pre-line mb-2">{access[k].notes}</p>}
            <div className="flex flex-wrap gap-2">{access[k].media.map((m, i) => <MediaThumb key={m.path} m={m} testid={`job-access-${k}-media-${i}`} />)}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

export const remapUnload = (oldRooms, newRooms, unload) => {
  const out = { ...(unload || {}) };
  if ((oldRooms || []).length === newRooms.length) {
    newRooms.forEach((r, i) => {
      const prev = oldRooms[i].room_name;
      if (prev !== r.room_name && out[prev] !== undefined) { out[r.room_name] = out[prev]; delete out[prev]; }
    });
  }
  return out;
};

export function UnloadEditor({ rooms, unload, onChange }) {
  if (!rooms?.length) return <p className="text-sm text-muted-foreground">Add rooms to your inventory above (from a video or “Add rooms manually”), then choose where each room should be unloaded.</p>;
  return (
    <div className="space-y-2" data-testid="unload-editor">
      {rooms.map((r, i) => (
        <div key={i} className="grid sm:grid-cols-[180px_1fr] items-center gap-2">
          <span className="text-sm font-medium flex items-center gap-1.5 truncate"><DoorOpen className="h-4 w-4 text-kinetic shrink-0" />{r.room_name}</span>
          <Input value={unload?.[r.room_name] || ""} maxLength={200} onChange={(e) => onChange({ ...(unload || {}), [r.room_name]: e.target.value })}
            placeholder="e.g. Bedroom at front of house" className="h-9 text-sm" data-testid={`unload-input-${i}`} />
        </div>
      ))}
    </div>
  );
}

export function FloorPlanField({ value, onChange }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const onFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (!(f.type.startsWith("image/") || f.type === "application/pdf")) { toast.error("Floor plan must be an image or PDF"); return; }
    if (f.size > MAX_PLAN) { toast.error("Floor plan must be 10MB or smaller"); return; }
    setBusy(true);
    try { const m = await uploadMedia(f); onChange({ path: m.path, content_type: m.content_type, filename: f.name }); toast.success("Floor plan added"); }
    catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-2" data-testid="floor-plan-field">
      <input ref={ref} type="file" accept="image/*,application/pdf" className="hidden" onChange={onFile} data-testid="floor-plan-input" />
      {value ? <FloorPlanView plan={value} onRemove={() => onChange(null)} /> : (
        <Button type="button" variant="outline" onClick={() => ref.current?.click()} disabled={busy} className="gap-2" data-testid="floor-plan-upload-btn">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <MapIcon className="h-4 w-4" />} Add new home floor plan
        </Button>
      )}
      <p className="text-xs text-muted-foreground">Optional · image or PDF, max 10MB. Only you and your accepted removalist can see it.</p>
    </div>
  );
}

export function FloorPlanView({ plan, onRemove }) {
  const src = `${API}/files/${plan.path}`;
  return (
    <div className="flex items-center gap-3 rounded-lg border border-border p-2 pr-3 w-fit max-w-full" data-testid="floor-plan-view">
      {plan.content_type?.startsWith("image/")
        ? <a href={src} target="_blank" rel="noreferrer"><img src={src} alt="Floor plan" className="h-16 w-16 rounded object-cover border border-border" /></a>
        : <a href={src} target="_blank" rel="noreferrer" className="h-16 w-16 rounded bg-kinetic-light flex items-center justify-center"><FileText className="h-7 w-7 text-kinetic" /></a>}
      <a href={src} target="_blank" rel="noreferrer" className="text-sm font-medium truncate hover:text-kinetic" data-testid="floor-plan-link">{plan.filename || "Floor plan"}</a>
      {onRemove && <button type="button" onClick={onRemove} data-testid="floor-plan-remove" className="h-6 w-6 rounded-full hover:bg-muted flex items-center justify-center text-muted-foreground"><X className="h-3.5 w-3.5" /></button>}
    </div>
  );
}

export function DownloadInventoryButton({ jobId, className = "" }) {
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try {
      const res = await api.get(`/jobs/${jobId}/inventory-pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const name = (res.headers["content-disposition"] || "").match(/filename="([^"]+)"/)?.[1] || "MoveTender-Inventory.pdf";
      const a = document.createElement("a");
      a.href = url; a.download = name; document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch { toast.error("Could not download the inventory"); } finally { setBusy(false); }
  };
  return (
    <Button type="button" onClick={go} disabled={busy} data-testid="download-inventory-btn" className={`gap-2 bg-kinetic hover:bg-kinetic-hover text-white ${className}`}>
      {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} Download inventory PDF
    </Button>
  );
}

export function MovePlanCard({ job, editable, onSaved }) {
  const [editing, setEditing] = useState(false);
  const init = () => ({ rooms: job.inventory?.rooms || [], plan: job.move_plan || { unload: {}, floor_plan: null }, access: { ...emptyAccess(), ...(job.access || {}) } });
  const [rooms, setRoomsRaw] = useState(init().rooms);
  const [plan, setPlan] = useState(init().plan);
  const [access, setAccess] = useState(init().access);
  const [saving, setSaving] = useState(false);
  const setRooms = (next) => { setPlan((p) => ({ ...p, unload: remapUnload(rooms, next, p.unload) })); setRoomsRaw(next); };
  const cancel = () => { const s = init(); setRoomsRaw(s.rooms); setPlan(s.plan); setAccess(s.access); setEditing(false); };
  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/jobs/${job.id}/move-plan`, { rooms, move_plan: plan, access });
      toast.success("Move plan saved"); setEditing(false); onSaved();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setSaving(false); }
  };
  const mp = job.move_plan || {};
  return (
    <Card className="p-6 space-y-5" data-testid="move-plan-card">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="font-display font-semibold text-lg">Move plan</h3>
          <p className="text-sm text-muted-foreground">Unload locations and floor plan — visible to you and your accepted removalist only.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {editable && !editing && <Button type="button" variant="outline" onClick={() => setEditing(true)} data-testid="edit-move-plan-btn">Edit move plan</Button>}
          {!editing && <DownloadInventoryButton jobId={job.id} />}
        </div>
      </div>
      {editing ? <MovePlanForm {...{ job, rooms, setRooms, plan, setPlan, access, setAccess }} /> : (
        <div className="space-y-4">
          {job.inventory?.rooms?.length > 0 && (
            <div className="space-y-1.5" data-testid="unload-summary">
              {job.inventory.rooms.map((r, i) => (
                <p key={i} className="text-sm flex flex-wrap gap-x-2"><span className="font-medium flex items-center gap-1.5"><DoorOpen className="h-4 w-4 text-kinetic" />{r.room_name}</span>
                  <span className="text-muted-foreground">→ {mp.unload?.[r.room_name] || "Not specified"}</span></p>
              ))}
            </div>
          )}
          {mp.floor_plan ? <FloorPlanView plan={mp.floor_plan} /> : <p className="text-sm text-muted-foreground">No floor plan added.</p>}
        </div>
      )}
      {editing && (
        <div className="flex gap-2">
          <Button type="button" onClick={save} disabled={saving} className="bg-kinetic hover:bg-kinetic-hover text-white" data-testid="save-move-plan-btn">{saving ? "Saving…" : "Save move plan"}</Button>
          <Button type="button" variant="ghost" onClick={cancel} data-testid="cancel-move-plan-btn">Cancel</Button>
        </div>
      )}
    </Card>
  );
}

function MovePlanForm({ job, rooms, setRooms, plan, setPlan, access, setAccess }) {
  return (
    <div className="space-y-6">
      <div className="space-y-2"><Label>Inventory items</Label><InventoryReview rooms={rooms} onChange={setRooms} /></div>
      <div className="space-y-2"><Label>Where should each room be unloaded?</Label><UnloadEditor rooms={rooms} unload={plan.unload} onChange={(u) => setPlan({ ...plan, unload: u })} /></div>
      <div className="space-y-2"><Label>New home floor plan</Label><FloorPlanField value={plan.floor_plan} onChange={(fp) => setPlan({ ...plan, floor_plan: fp })} /></div>
      <div className="space-y-2"><Label>Access details</Label><AccessEditor value={access} onChange={setAccess} /></div>
    </div>
  );
}
